import type { TranslationKeys } from '@vocab/core';
import type { ReactElement, ComponentProps, FC } from 'react';
import { Role } from '../Role';
import { Education } from '../Education';
import { Licence } from '../Licence';
import { ApplicationQuestions } from '../ApplicationQuestions';
import {
  Alert,
  Box,
  Column,
  Columns,
  Heading,
  IconInfo,
  IconNewWindow,
  Inline,
  Stack,
  Tag,
  Text,
  TextLink,
  TextLinkButton,
  TooltipRenderer,
} from 'braid-design-system';
import translations from './.vocab';
import type { AttachmentTypeName } from 'src/constants/attachmentTypes';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';
import { useTranslations } from '@vocab/react';
import downloadFile from 'src/DocumentViewer/utils/downloadFile';
import {
  trackAlertDismissEvent,
  trackDownloadDocumentEvent,
} from 'src/utils/events';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import { Display } from 'src/libs/Display';
import { ProfileDetails } from '../ProfileDetails';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { formatSalaryValues } from 'src/libs/formatSalaryValues';
import { SeekPassIconVerified } from '@seek/certsy-ui';
import urls from 'src/constants/urls';
import { getAdvertiserIds } from 'src/utils/advertiserIds';
import { useSalaryQuestionnaireQuery } from './ProfileBody.hirer.generated';
import { errorCodes } from 'src/constants/errorTypes';
import { logError } from 'src/utils/logError';
import type { SalaryType } from '../../graphql/cm-types.generated';

export type AttachmentType =
  | 'RESUME'
  | 'COVER_LETTER'
  | 'SELECTION_CRITERIA'
  | 'OTHER';

const getLabelFromAttachmentType = (
  type: AttachmentType,
): TranslationKeys<typeof translations> => {
  switch (type) {
    case 'RESUME':
      return 'Resumé';
    case 'COVER_LETTER':
      return 'Cover Letter';
    case 'SELECTION_CRITERIA':
      return 'Selection Criteria';
    case 'OTHER':
      return 'Cover Letter';
  }
};

const ProfileSection: FC<{
  title: string | ReactElement;
  children: ReactElement;
}> = ({ title, children }) => (
  <Stack space="medium">
    {typeof title === 'string' ? <Heading level="4">{title}</Heading> : title}
    {children}
  </Stack>
);

interface ProfileBodyProps {
  jobApplicationId: string;
  questionnaireId: string | undefined;
  applicationDocuments: {
    attachments: Array<{
      id: string;
      type: AttachmentType;
      filename: string | null;
      typeName: AttachmentTypeName | undefined;
      downloadUrl: string;
    }>;
    tracking: {
      candidateId: string;
      prospectId: string;
      isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
    };
  };
  roles: Array<ComponentProps<typeof Role>>;
  educationItems: Array<ComponentProps<typeof Education>>;
  verifiedIdentity: {
    fullName: string;
  } | null;
  licences: Array<ComponentProps<typeof Licence>>;
  employmentStatus: {
    nationalities: string[];
    rightsToWork: string[];
    expectedSalaries: Array<{
      salaryType: SalaryType;
      minimumSalaryValue: number;
      maximumSalaryValue: number | null;
      currency: string;
    }>;
  };
  applicationQuestions: ComponentProps<typeof ApplicationQuestions>;
  skills: string[];
  shouldShowSEEKPassDisclaimer: boolean;
}

export const ProfileBody: FC<ProfileBodyProps> = ({
  jobApplicationId,
  questionnaireId,
  applicationDocuments,
  roles,
  educationItems,
  verifiedIdentity,
  licences,
  employmentStatus,
  applicationQuestions,
  skills,
  shouldShowSEEKPassDisclaimer,
}) => {
  const { t } = useTranslations(translations);
  const advertiserId = getAdvertiserIds().advertiserId;

  const { getCertsyVerifiedFullNameAlert, hideCertsyVerifiedFullNameAlert } =
    useUserActionStatus('certsy-verified-full-name-alert');

  const { site, locale } = useAppConfig();

  const {
    loading: loadingSalaryQuestionnaire,
    data,
    error,
  } = useSalaryQuestionnaireQuery({
    variables: {
      questionnaireId: questionnaireId ?? '',
      adcentreAdvertiserId: advertiserId ?? '',
    },
    onError(err) {
      logError(
        {
          errorCode: errorCodes.GET_EXPECTED_SALARY,
          description: 'Error attempting: fetch expected salary data',
        },
        {
          err,
          questionnaireId,
          adcentreAdvertiserId: advertiserId,
        },
      );
    },
    skip:
      employmentStatus.expectedSalaries.length === 0 ||
      !questionnaireId ||
      !advertiserId,
  });

  const salaryQuestions = Boolean(error)
    ? []
    : data?.questionnaire?.salaryQuestions || [];

  const shouldDisplayProfileSalarySection = salaryQuestions.length === 0;

  const shouldShowNationalities = employmentStatus.nationalities.length > 0;
  const shouldShowRightsToWork =
    (site === 'employer-seek-my' ||
      site === 'employer-seek-sg' ||
      site === 'employer-seek-hk' ||
      site === 'employer-seek-th') &&
    employmentStatus.rightsToWork.length > 0;
  const shouldShowExpectedSalary =
    !loadingSalaryQuestionnaire &&
    shouldDisplayProfileSalarySection &&
    (site === 'employer-seek-my' ||
      site === 'employer-seek-sg' ||
      site === 'employer-seek-ph' ||
      site === 'employer-seek-id' ||
      site === 'employer-seek-hk' ||
      site === 'employer-seek-th') &&
    employmentStatus.expectedSalaries.length > 0;

  return (
    <Box
      style={{ maxWidth: 710 }}
      paddingBottom="xlarge"
      data={{ cy: 'profile-data' }}
    >
      {/* FIXME: move this back in the stack when we move to Braid v33 */}
      {applicationDocuments.attachments.length > 0 ? (
        <Display mobile>
          <Box paddingBottom="xlarge">
            <ProfileSection title={t('Application documents')}>
              <Stack space="small">
                {applicationDocuments.attachments.map((attachment) => {
                  const attachmentLabel = attachment.filename
                    ? attachment.filename
                    : t(getLabelFromAttachmentType(attachment.type));

                  const onClickAttachment = async () => {
                    await downloadFile(attachment.downloadUrl, () => {});
                    trackDownloadDocumentEvent({
                      candidateId: applicationDocuments.tracking.candidateId,
                      prospectId: applicationDocuments.tracking.prospectId,
                      documentType: attachment.typeName,
                      attachmentId: attachment.id,
                      currentPage: TRACKING_CANDIDATE_DETAILS,
                      isVerifiedId: applicationDocuments.tracking.isVerifiedId,
                    });
                  };

                  return (
                    <Text baseline={false} key={attachment.id} size="small">
                      {/* FIXME: this label can get very long on mobile which makes the width of the parent larger than the mobile width*/}
                      <TextLinkButton onClick={onClickAttachment}>
                        {attachmentLabel}
                      </TextLinkButton>
                    </Text>
                  );
                })}
              </Stack>
            </ProfileSection>
          </Box>
        </Display>
      ) : null}
      <Stack space="xlarge">
        {roles.length > 0 ? (
          <ProfileSection title={t('Career history')}>
            <Stack space="large">
              {roles.map((role, index) => (
                <Role
                  key={index}
                  title={role.title}
                  company={role.company}
                  from={role.from}
                  to={role.to}
                  achievements={role.achievements}
                />
              ))}
            </Stack>
          </ProfileSection>
        ) : null}

        {educationItems.length > 0 ? (
          <ProfileSection title={t('Education')}>
            <Stack space="large">
              {educationItems.map((education) => (
                <Education
                  key={education.id}
                  id={education.id}
                  title={education.title}
                  institute={education.institute}
                  completed={education.completed}
                  completionDate={education.completionDate}
                  highlights={education.highlights}
                  verificationViewFields={education.verificationViewFields}
                />
              ))}
            </Stack>
          </ProfileSection>
        ) : null}

        {licences.length > 0 ? (
          <ProfileSection title={t('Licences and certifications')}>
            <Stack space="large">
              {licences.map((licence) => (
                <Licence
                  key={licence.id}
                  id={licence.id}
                  title={licence.title}
                  issuingOrganisation={licence.issuingOrganisation}
                  formattedDate={licence.formattedDate}
                  description={licence.description}
                  verificationViewFields={licence.verificationViewFields}
                />
              ))}
            </Stack>
          </ProfileSection>
        ) : null}

        {verifiedIdentity ? (
          <ProfileSection
            title={
              <Inline space="xsmall" alignY="center">
                <Heading level="4">{t('Digital ID')}</Heading>
                <TooltipRenderer
                  id={`${jobApplicationId}-digital-id-tooltip`}
                  tooltip={
                    <Text>
                      {t(
                        'A verified identity means this person has confirmed their identity with the SEEK Pass verification platform using a government-issued document.',
                      )}
                    </Text>
                  }
                  placement="top"
                >
                  {({ triggerProps }) => (
                    <Box {...triggerProps}>
                      <Text size="small">
                        <IconInfo />
                      </Text>
                    </Box>
                  )}
                </TooltipRenderer>
              </Inline>
            }
          >
            <Stack space="small">
              <ProfileDetails
                details={[
                  {
                    name: t('Name'),
                    values: [verifiedIdentity.fullName],
                    showVerifiedIcon: true,
                  },
                ]}
              />

              {getCertsyVerifiedFullNameAlert() === 'visible' && (
                <Alert
                  tone="info"
                  onClose={() => {
                    trackAlertDismissEvent({
                      alertShownMessageCode:
                        'Candidate Management - Verified ID Banner',
                    });
                    hideCertsyVerifiedFullNameAlert();
                  }}
                  closeLabel={t('Close')}
                >
                  <Text size="small">
                    {t(
                      'A verified identity means this person has confirmed their identity with the SEEK Pass verification platform using a government-issued document. This may differ from their SEEK profile name as they may be using a preferred name.',
                    )}
                  </Text>
                </Alert>
              )}
            </Stack>
          </ProfileSection>
        ) : null}

        {shouldShowNationalities ||
        shouldShowRightsToWork ||
        shouldShowExpectedSalary ? (
          <ProfileSection title={t('Employment status')}>
            <Stack space="medium">
              {shouldShowNationalities ? (
                <ProfileDetails
                  details={[
                    {
                      name: t('Nationality'),
                      values: employmentStatus.nationalities,
                    },
                  ]}
                />
              ) : null}

              {shouldShowRightsToWork ? (
                <ProfileDetails
                  details={[
                    {
                      name: t('Right to work'),
                      values: employmentStatus.rightsToWork,
                    },
                  ]}
                />
              ) : null}

              {shouldShowExpectedSalary ? (
                <ProfileDetails
                  details={[
                    {
                      name: t('Salary expectation'),
                      values: employmentStatus.expectedSalaries.map(
                        ({
                          currency,
                          salaryType,
                          minimumSalaryValue,
                          maximumSalaryValue,
                        }) =>
                          `${formatSalaryValues({
                            minimumSalaryValue,
                            maximumSalaryValue,
                            currency,
                            locale,
                          })} ${salaryType !== null && t(salaryType)}`,
                      ),
                    },
                  ]}
                />
              ) : null}
            </Stack>
          </ProfileSection>
        ) : null}

        {applicationQuestions.questions.length > 0 ? (
          <ProfileSection title={t('Application questions')}>
            <ApplicationQuestions
              questions={applicationQuestions.questions}
              allVerifications={applicationQuestions.allVerifications}
            />
          </ProfileSection>
        ) : null}

        {skills.length > 0 ? (
          <ProfileSection title={t('Skills')}>
            <Inline space="small">
              {skills.map((skill) => (
                <Tag key={skill} size="small">
                  {skill}
                </Tag>
              ))}
            </Inline>
          </ProfileSection>
        ) : null}

        {shouldShowSEEKPassDisclaimer ? (
          <Columns space="xxsmall">
            <Column width="content">
              <Text size="xsmall">
                <SeekPassIconVerified />
              </Text>
            </Column>
            <Column>
              <Text size="small">
                {t(
                  'Verified credentials can help you assess with confidence. However, as an employer you remain responsible for ensuring candidates meet legal requirements.',
                )}
              </Text>
              <Box paddingTop="medium">
                <Text size="small">
                  <TextLink
                    href={urls.CERTSY_OFFICIAL_LINK}
                    target="_blank"
                    rel="noopener"
                    icon={<IconNewWindow />}
                    iconPosition="trailing"
                  >
                    {t('Find out more')}
                  </TextLink>
                </Text>
              </Box>
            </Column>
          </Columns>
        ) : null}
      </Stack>
    </Box>
  );
};
